import { selectWarehouseAllStates, selectWarehouseSelectedGateGateway, updateSelectedGateGateway } from 'containers/Warehouses/warehousesSlice'
import CheckingLocationPopup from 'components/popups/CheckingLocationPopup'
import { getGateStatusLabel } from 'containers/entrance/core/gateLabel'
import { GATEWAY_STATUS, GATE_STATUS, STATUS } from 'utils/constants'
import { ReactComponent as MagIcon } from 'assets/magDetails.svg'
import { ReactComponent as HelpIcon } from 'assets/helpBig.svg'
import { ReactComponent as GateIcon } from 'assets/gate.svg'
import { ReactComponent as DoorIcon } from 'assets/door.svg'
import { useReduxDispatch, useReduxSelector } from 'store'
import BigCircleBtn from 'components/buttons/BigCircleBtn'
import { selectConfig, selectLightTheme } from 'appSlice'
import Gateway from 'containers/entrance/core/Gateway'
import SelectGateModal from '../core/SelectGateModal'
import CircleBtn from 'components/buttons/CircleBtn'
import useTranslation from 'hooks/useTranslation'
import Gate from 'containers/entrance/core/Gate'
import usePopupState from 'hooks/usePopupState'
import { useHistory } from 'react-router-dom'
import LoginFormPopup from './LoginFormPopup'
import MagNumber from 'components/MagNumber'
import Warehouse from 'components/Warehouse'
import useData from '../core/hooks/useData'
import { DEFAULT_PREFIX } from 'utils/phone'
import { POST } from 'rest/request'
import { useState } from 'react'
import { api } from 'rest/urls'
import '../style.scss'

export interface LoginForm {
  prefix: string
  phone: string
  pin: string
}

type BaseType = 'gates' | 'gateways'
type Type = BaseType | 'warehouse'
const appType = process.env.REACT_APP_TYPE

const Shared = () => {
  const config = useReduxSelector(selectConfig)
  const [type, setType] = useState<Type>('gates')
  const lightTheme = useReduxSelector(selectLightTheme)
  const states = useReduxSelector(selectWarehouseAllStates)
  const [loginForm, setLoginForm] = useState<LoginForm>({
    prefix: DEFAULT_PREFIX,
    phone: '',
    pin: ''
  })

  const { isLoading, data } = useData()
  const [isSending, setSending] = useState(false)
  const selected = useReduxSelector(state => selectWarehouseSelectedGateGateway(state, data?.placeId || ''))
  const { popup, openPopup } = usePopupState<{ type: 'loginForm' | 'checkingGateLocation' | 'selectGateOrGateway', props: { type: Type, selectedId?: string, enabled?: boolean } }>()

  const dispatch = useReduxDispatch()
  const { push, goBack } = useHistory()
  const { t } = useTranslation('entrance')

  const action = async <T extends Type>(_type: T, objectId?: string, form = loginForm) => {
    if (!data || isSending) return
    try {
      const additionalBody = data.needPin ? form : undefined
      const action = (_type === 'gates' || _type === 'gateways')
        ? data?.actions?.[_type as BaseType]?.[objectId || '']?.open
        : data?.actions?.warehouse?.open
      if (!action) throw new Error('No action')
      if (action.type === 'request' && action.method === 'POST') {
        setSending(true)
        const body = additionalBody
          ? { ...action.payload, ...additionalBody }
          : action.payload
        const response = await POST(`${api}${action.url}`, { body })
        setSending(false)
        if (response?.result === 'ok') {
          if (popup) {
            setLoginForm(form)
            goBack()
          }
        } else {
          setLoginForm({ prefix: DEFAULT_PREFIX, phone: '', pin: '' })
          throw new Error('Wrong response')
        }

      } else {
        throw new Error('No supported action')
      }
    } catch (error) {
      setSending(false)
      console.error(error)
    }
  }

  const open = <T extends Type>(_type: T, options?: { selectedId?: string, enabled?: boolean, switchModal?: boolean }) => {
    if (isSending) return
    if ((config?.warehouseLocationCheckEnabled && data?.placeGps) ? !options?.enabled : false) {
      openPopup({ type: 'checkingGateLocation', props: { type: _type, selectedId: options?.selectedId } })

    } else if (!options?.selectedId && (_type === 'gates' || _type === 'gateways') && Object.keys(data?.[_type as BaseType] || {}).length > 1) {
      openPopup({ type: 'selectGateOrGateway', props: { type: _type, enabled: options?.enabled } }, options?.switchModal)

    } else if (data?.needPin && (!loginForm.phone || !loginForm.pin)) {
      openPopup({ type: 'loginForm', props: { type: _type, selectedId: options?.selectedId } }, options?.switchModal)

    } else {
      if (options?.switchModal) goBack()
      action(_type, options?.selectedId)
    }
  }

  const help = <CircleBtn
    text={t('help')}
    Icon={HelpIcon}
    action={() => {
      push('/help')
    }}
  />

  const magNumber = data?.number
    ? <MagNumber number={data.number} />
    : null

  return (
    <>
      {popup?.type === 'checkingGateLocation' && <CheckingLocationPopup
        targetPosition={(Array.isArray(data?.placeGps) && data?.placeGps.length === 2) ? {
          lat: Number(data?.placeGps[0]),
          lng: Number(data?.placeGps[1])
        } : undefined}
        callback={(success) => {
          if (success) {
            open(popup.props.type, { selectedId: popup.props?.selectedId, enabled: true, switchModal: true })
          } else {
            goBack()
          }
        }}
      />}
      {popup?.type === 'selectGateOrGateway' && <SelectGateModal
        isGate={popup.props.type === 'gates'}
        options={Object.values(data?.[popup.props.type as BaseType] || {})}
        callback={objectId => {
          if (data?.placeId) {
            dispatch(updateSelectedGateGateway({ placeId: data?.placeId, object: popup.props.type === 'gates' ? 'gate' : 'gateway', objectId }))
          }
          open(popup.props.type, { selectedId: objectId, enabled: popup.props.enabled, switchModal: true })
        }}
      />}
      {popup?.type === 'loginForm' && <LoginFormPopup
        isSending={isSending}
        loginForm={loginForm}
        login={form => {
          action(popup.props.type, popup.props.selectedId, form)
        }}
      />}

      {(() => {
        switch (type) {
          case 'gates': {
            const gateKeys = Object.keys(data?.gates || {})
            const firstGateKey = gateKeys[0]
            const gateId = selected?.gate || firstGateKey
            const socketGate = gateId && data?.placeId && states
              ? states.places?.[data.placeId]?.gates?.[gateId]
              : undefined
            const socketStatus = socketGate?.state
            const status = socketStatus || data?.gates?.[gateId]?.state || 'gate_closed'
            const gateMotion = socketStatus ? socketGate : data?.gates?.[gateId]

            return (
              <div className='entrance warehouse --full'>
                <div className='entrance__label'>
                  {magNumber}
                  <span>{data?.address}</span>
                </div>

                <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
                  <Gate
                    status={status}
                    motion={gateMotion}
                    onReportProblem={() => push('/help')}
                  />
                </div>

                <div className='entrance__menu menu__main'>
                  <div className='menu__status'>
                    <div className='menu__status--margin' />
                    <div className='status__options'>
                      <div className='status__option'>
                        <i className={`icon icon--padlock-${status === GATE_STATUS.opened ? 'open' : 'closed'}`} />
                        <span>
                          {t(getGateStatusLabel(status))}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='menu__buttons --top'>
                    {(appType === 'rze01' || appType === 'chr01')
                      ? help
                      : <CircleBtn
                        disabled={!data?.actions?.gateways}
                        text={t('openGateway')}
                        Icon={DoorIcon}
                        action={() => {
                          setType('gateways')
                        }} />}

                    <div className='buttons__col center'>
                      <BigCircleBtn
                        disabled={isSending || isLoading || !data?.actions?.gates}
                        text={t(isSending ? 'loading'
                          : status === GATE_STATUS.opening ? 'opening'
                            : status === GATE_STATUS.closing ? 'closing'
                              : status === GATE_STATUS.opened ? 'driveThroughGate'
                                : 'open')}
                        status={status.includes('ing') ? STATUS.pending : STATUS.idle}
                        action={() => {
                          if (/* (status === GATE_STATUS.closed || status === GATE_STATUS.closing) && */data?.actions?.gates?.[gateId]?.open) {
                            if (gateKeys.length === 1) {
                              open(type, { selectedId: firstGateKey })
                            } else {
                              open(type)
                            }
                          }
                        }}
                      />
                      {(appType !== 'rze01' && appType !== 'chr01') && help}
                    </div>

                    <CircleBtn
                      disabled={!data?.actions?.warehouse}
                      text={t(`openMag.${appType}`)}
                      Icon={MagIcon}
                      action={() => {
                        setType('warehouse')
                      }} />
                  </div>
                </div>
              </div>
            )
          }

          case 'gateways': {
            const gatewayKeys = Object.keys(data?.gateways || {})
            const firstGatewayKey = gatewayKeys[0]
            const gatewayId = selected?.gateway || firstGatewayKey
            const socketStatus = gatewayId && data?.placeId && states
              ? states.places?.[data.placeId]?.gateways?.[gatewayId]?.state
              : undefined
            const status = socketStatus || data?.gateways?.[gatewayId]?.state || 'gateway_closed'

            return (
              <div className='entrance warehouse --full'>
                <div className='entrance__label'>
                  {magNumber}
                  <span>{data?.address}</span>
                </div>

                <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
                  <Gateway status={status} />
                </div>

                <div className='entrance__menu menu__main'>
                  <div className='menu__status'>
                    <div className='menu__status--margin' />
                    <div className='status__options'>
                      <div className='status__option'>
                        <i className={`icon icon--padlock-${(status === GATEWAY_STATUS.opened || status === GATEWAY_STATUS.unlocked) ? 'open' : 'closed'}`} />
                        <span>
                          {t(status === GATEWAY_STATUS.opened
                            ? 'wicketOpen'
                            : status === GATEWAY_STATUS.unlocked
                              ? 'wicketLatchReleased'
                              : 'wicketClosed')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className='menu__buttons --top'>
                    <CircleBtn
                      disabled={!data?.actions?.gates}
                      text={t('openGate')}
                      Icon={GateIcon}
                      action={() => {
                        setType('gates')
                      }} />

                    <div className='buttons__col center'>
                      <BigCircleBtn
                        disabled={isSending || isLoading || !data?.actions?.gateways}
                        text={t(isSending ? 'loading' : status === GATEWAY_STATUS.closed ? 'open' : 'enterThroughGate')}
                        status={status === GATEWAY_STATUS.opened ? STATUS.succeed : STATUS.idle}
                        action={() => {
                          if (/* status === GATEWAY_STATUS.closed &&  */data?.actions?.gateways?.[gatewayId]?.open) {
                            if (gatewayKeys.length === 1) {
                              open(type, { selectedId: firstGatewayKey })
                            } else {
                              open(type)
                            }
                          }
                        }} />
                      {help}
                    </div>

                    <CircleBtn
                      disabled={!data?.actions?.warehouse}
                      text={t(`openMag.${appType}`)}
                      Icon={MagIcon}
                      action={() => {
                        setType('warehouse')
                      }} />
                  </div>
                </div>
              </div>
            )
          }

          case 'warehouse': {
            const socketStatus = data?.id && states ? states.warehouses[data.id] : undefined
            const door = socketStatus?.door || data?.states.door || 'closed'
            const lock = socketStatus?.lock || data?.states.lock || 'locked'

            return (
              <div className='entrance warehouse --full'>
                <div className='entrance__label'>
                  {magNumber}
                  <span>{data?.address}</span>
                </div>

                <div className={`entrance__main center${lightTheme ? '' : ' --shadow'}`}>
                  <Warehouse
                    door={door}
                    lock={lock}
                  />
                </div>

                <div className='entrance__menu menu__main'>
                  <div className='menu__status'>
                    <div className='menu__status--margin' />
                    <div className='status__options'>
                      <div className={`status__option${lock === 'unlocked' ? ' --important' : ''}`}>
                        <i className={`icon icon--padlock-${door === 'opened' || lock === 'unlocked' ? 'open' : 'closed'}`} />
                        <span>{t(door === 'opened' ? 'doorsOpen' : lock === 'unlocked' ? 'latchOpen' : 'doorsClosed')}</span>
                      </div>
                    </div>
                  </div>

                  <div className='menu__buttons --top'>
                    <CircleBtn
                      disabled={!data?.actions?.gates}
                      text={t('openGate')}
                      Icon={GateIcon}
                      action={() => {
                        setType('gates')
                      }} />

                    <div className='buttons__col center'>
                      <BigCircleBtn
                        disabled={isSending || isLoading || !data?.actions?.warehouse?.open}
                        text={door === 'opened'
                          ? t('doorOpen')
                          : lock === 'unlocked'
                            ? t('pullTheDoor')
                            : t('open')}
                        status={door === 'opened'
                          ? STATUS.succeed
                          : lock === 'unlocked'
                            ? STATUS.pending
                            : STATUS.idle}
                        action={() => {
                          if (lock === 'locked' && data?.actions?.warehouse?.open) {
                            open(type)
                          }
                        }}
                      />
                      {help}
                    </div>

                    <CircleBtn
                      disabled={!data?.actions?.gateways}
                      text={t('openGateway')}
                      Icon={DoorIcon}
                      action={() => {
                        setType('gateways')
                      }} />
                  </div>
                </div>
              </div>
            )
          }
        }
      })()}
    </>
  )
}

export default Shared